import { useTranslation } from 'react-i18next'

import { FlexBox } from 'components/Box'
import { COLUMN_SIZE } from 'components/Table/constants'
import Tooltip from 'components/Tooltip'
import { ReportStatusChip } from 'features/yieldbirdWrapperAgent/components/ReportStatusChip'
import { displayUrl } from 'features/yieldbirdWrapperAgent/utils/url'
import { DATE_FORMAT, formatDate } from 'utils/date'
import { OpenDiagnosticsButton } from './OpenDiagnosticsButton'
import { ReportLinkButton } from './ReportLinkButton'

import type { ReportRow } from './types'
import type { ColumnDef } from '@tanstack/react-table'
import type { Report, ReportFormat } from 'features/yieldbirdWrapperAgent/api/types'

const EMPTY_CELL = '-'

type ReportLink = Report['reportLinks'][ReportFormat]

const renderFileCell = (href: ReportLink): React.JSX.Element | string =>
  href === null ? EMPTY_CELL : <ReportLinkButton href={href} />

export const useColumns = (): ColumnDef<ReportRow>[] => {
  const { t } = useTranslation('features/yieldbirdWrapperAgent')

  return [
    {
      accessorKey: 'createdAt',
      cell: ({ row }) => formatDate(new Date(row.original.createdAt), DATE_FORMAT.WITH_TIME, true),
      header: t('reports.header.createdAt'),
    },
    {
      accessorKey: 'url',
      cell: ({ row }) => (
        <Tooltip content={row.original.url}>
          <span>{displayUrl(row.original.url)}</span>
        </Tooltip>
      ),
      header: t('reports.header.url'),
    },
    {
      accessorKey: 'status',
      cell: ({ row }) => (
        <FlexBox
          axis='x'
          sx={{ alignItems: 'center' }}
        >
          <ReportStatusChip status={row.original.status} />

          <Tooltip content={t(`status.${row.original.status}`)} />
        </FlexBox>
      ),
      header: t('reports.header.status'),
    },
    {
      accessorKey: 'diagnostics',
      cell: ({ row }) => <OpenDiagnosticsButton jobId={row.original.jobId} />,
      header: t('reports.header.diagnostics'),
      meta: { centered: true },
      size: COLUMN_SIZE.XS,
    },
    {
      accessorFn: ({ reportLinks }: ReportRow): ReportLink => reportLinks.html,
      cell: ({ row }) => renderFileCell(row.original.reportLinks.html),
      header: t('reports.header.html'),
      id: 'html',
      meta: { centered: true },
      size: COLUMN_SIZE.XS,
    },
    {
      accessorFn: ({ reportLinks }: ReportRow): ReportLink => reportLinks.json,
      cell: ({ row }) => renderFileCell(row.original.reportLinks.json),
      header: t('reports.header.json'),
      id: 'json',
      meta: { centered: true },
      size: COLUMN_SIZE.XS,
    },
  ]
}
